import analyticstracker from "@jmc/analyticstracker";
import { EventTypes } from "@jmc/core/src/types/EventTypes";
import { Icon } from "@jmc/solid-design-system/src/components/atoms/Icon/Icon";
import { Typography } from "@jmc/solid-design-system/src/components/atoms/Typography/Typography";
import { useJnjBranding } from "@jmc/utils/hooks/useJnjBranding";
import { useOnScreen } from "@jmc/utils/hooks/useOnScreen";
import { cleanCSSIdentifier } from "@jmc/utils/utils/clean-css-identifier";
import { mdiClose, mdiMapMarker } from "@mdi/js";
import classnames from "classnames";
import React, { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { CustomMarker, GeoSearch } from "react-instantsearch-dom-maps";

import BrandedHCPMarkerIcon from "-!react-svg-loader!./svgs/branded-hcp-marker-icon.svg";
import BrandedHospitalMarkerIcon from "-!react-svg-loader!./svgs/branded-hospital-marker-icon.svg";
import BrandedPCMarkerIcon from "-!react-svg-loader!./svgs/branded-pc-marker-icon.svg";
import HCPMarkerIcon from "-!react-svg-loader!./svgs/hcp-marker-icon.svg";
import HospitalMarkerIcon from "-!react-svg-loader!./svgs/hospital-marker-icon.svg";
import PCMarkerIcon from "-!react-svg-loader!./svgs/pc-marker-icon.svg";

import { useLocatorContext } from "../LocatorContext/LocatorContext";
import { LocationData } from "../types/LocationData";
import style from "./style.module.scss";

const ZOOM_THRESHOLD = 13;

interface Marker {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getMap?: () => any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getPosition?: () => any;
}

interface ClickMarkerData {
    activeHit: LocationData;
    marker: Marker;
    hits: LocationData[];
    hideInfoPanel?: boolean;
}

interface Props {
    name: string;
    placement: string;
    searchFrom?: string;
    hideMap?: boolean;
}

export const getMarkerId = (objectID: string): string => {
    return cleanCSSIdentifier(`Marker-${objectID}`);
};

export const Geo = ({ name, placement, hideMap = false }: Props): JSX.Element => {
    const {
        setActiveHit,
        mapFullScreen,
        setMapFullScreen,
        setInfoPanelVisible,
        trackingName,
        setTrackingName,
        google,
    } = useLocatorContext();
    const { t } = useTranslation();
    const ref = useRef<HTMLDivElement>(null);
    const inViewport = useOnScreen(ref);
    const { jnjFullBranded } = useJnjBranding();

    useEffect(() => {
        if (inViewport) {
            analyticstracker().trackEvent({
                event: EventTypes.MAP_IMPRESSION,
                info: {
                    name,
                    component: "map",
                    placement,
                },
            });
        }
    }, [inViewport]);

    useEffect(() => {
        if (trackingName && trackingName === "marker") {
            analyticstracker().trackEvent({
                event: EventTypes.MAP_CLICK,
                info: {
                    name: name,
                    component: "marker",
                    placement: placement,
                    targettype: "internal",
                    targeturl: window?.location?.origin,
                },
            });
        }

        return (): void => {
            setTrackingName(null);
        };
    }, [trackingName]);

    // Using react-svg-loader we can import SVGs as compoments and change their colors with CSS.
    const MarkerIcon = ({ type }: { type: string }): JSX.Element => {
        const icon = (): JSX.Element => {
            switch (type?.toLowerCase()) {
                case "patient-center":
                    return jnjFullBranded ? <BrandedPCMarkerIcon /> : <PCMarkerIcon />;
                case "hospital":
                    return jnjFullBranded ? <BrandedHospitalMarkerIcon /> : <HospitalMarkerIcon />;
                default:
                    return jnjFullBranded ? <BrandedHCPMarkerIcon /> : <HCPMarkerIcon />;
            }
        };

        return <div className={style.marker}>{icon()}</div>;
    };

    const onClickMarker = ({
        activeHit = null,
        marker = null,
        hits = null,
        hideInfoPanel = false,
    }: ClickMarkerData): void => {
        setTrackingName("marker");
        if (!marker) {
            setActiveHit(null);
            !hideInfoPanel && setInfoPanelVisible(false);
        } else {
            setActiveHit(activeHit);
            !hideInfoPanel && setInfoPanelVisible(true);
        }

        // Zoom and center the map on the marker.
        if (marker?.getMap()?.zoom < ZOOM_THRESHOLD) marker?.getMap()?.setZoom(ZOOM_THRESHOLD);
        marker?.getMap()?.panTo(marker?.getPosition());

        // Set active hit color.
        // Getting the elements with getElementId does not interfere with useMemo
        hits?.forEach((hit) => {
            const element = document?.getElementById(getMarkerId(hit.objectID));
            if (hit === activeHit) {
                element?.classList?.add(style.activeMarker);
            } else {
                element?.classList?.remove(style.activeMarker);
            }
        });
    };

    // useMemo avoids the map being rendering again in some cases, which causes to recenter the map.
    return useMemo(
        () => (
            <div
                ref={ref}
                className={classnames(
                    style.wrapper,
                    mapFullScreen ? style.fullScreen : null,
                    hideMap ? style.hide : null,
                )}
            >
                {!jnjFullBranded && (
                    <button className={style.showFullMap} onClick={() => setMapFullScreen(true)}>
                        <Typography color="primary-general">
                            {jnjFullBranded ? t("Show in map", { ns: "common" }) : t("Show full map", { ns: "common" })}
                        </Typography>
                        {!jnjFullBranded && <Icon size={"medium"} icon={mdiMapMarker} />}
                    </button>
                )}
                <button
                    className={style.closeMap}
                    onClick={() => setMapFullScreen(false)}
                    title={t("Close", { ns: "common" })}
                >
                    <Icon size={"large"} icon={mdiClose} />
                </button>
                <GeoSearch
                    google={google}
                    enableRefine={false}
                    streetViewControl={false}
                    mapTypeControl={false}
                    zoomControl={true}
                    zoomControlOptions={{ position: google?.maps?.ControlPosition?.RIGHT_BOTTOM }}
                >
                    {({ hits }: { hits: LocationData[] }) => (
                        <div>
                            {hits.map((hit: LocationData) => {
                                return (
                                    <CustomMarker
                                        key={hit.objectID}
                                        className={style.customMarker}
                                        hit={hit}
                                        onClick={({ event, marker }: { event: PointerEvent; marker: unknown }) => {
                                            onClickMarker({
                                                activeHit: hit,
                                                marker,
                                                hits,
                                                hideInfoPanel: !!event?.target?.dataset?.preventInfopanel,
                                            });
                                            event.stopPropagation();
                                        }}
                                    >
                                        <div id={getMarkerId(hit.objectID)}>
                                            <MarkerIcon type={hit.type} />
                                        </div>
                                    </CustomMarker>
                                );
                            })}
                        </div>
                    )}
                </GeoSearch>
            </div>
        ),
        [mapFullScreen, hideMap],
    );
};
